import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../style/css/authpage2026.css';
import managedata from '../services/managedata.js';
import logo from '../style/imges/hopalogo.png';

// AuthPage 2026 — OTP login + registration. LOGIN is passwordless: enter phone → we send a
// 6-digit code by SMS AND email (server: sendOTP with alsoEmail=true) → enter the code
// (loginWithOTP). REGISTER is a real 2-step OTP: fill details → registerUser sends a code by
// SMS AND email → enter the code → completeRegisterUser (verifies the REAL code, single-use,
// 10-min TTL, server-side). Codes expire and work exactly once.
const isValidPhone = (p) => /^05\d{8}$/.test(p || '');
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

const AuthPage = ({ onLogin }) => {
    const [mode, setMode] = useState('login'); // 'login' | 'register'
    const [loginStep, setLoginStep] = useState('phone'); // 'phone' | 'code'
    const [registerStep, setRegisterStep] = useState('details'); // 'details' | 'code'
    const [phone, setPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [password, setPassword] = useState('');
    // register-only fields
    const [privateName, setPrivateName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [confirm, setConfirm] = useState('');

    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const switchMode = (m) => { setMode(m); setError(''); setLoginStep('phone'); setRegisterStep('details'); setOtpCode(''); };

    // LOGIN step 1 — send the OTP (by SMS + email). sendOTP always returns 100 (anti-enumeration).
    const doSendOtp = () => {
        setError('');
        if (!isValidPhone(phone)) { setError('מספר טלפון לא תקין (05 ואחריו 8 ספרות)'); return; }
        setBusy(true);
        managedata.sendData(
            { data: { phone }, type: { act: 'sendOTP' } },
            () => { setBusy(false); setError('לא הצלחנו לשלוח קוד, נסו שוב'); },
            () => { setBusy(false); setLoginStep('code'); setOtpCode(''); },
        );
    };

    // LOGIN step 2 — verify the code.
    const doLoginOtp = () => {
        setError('');
        if (!/^\d{4,6}$/.test(otpCode)) { setError('הזינו את הקוד שקיבלתם'); return; }
        setBusy(true);
        managedata.sendData(
            { data: { phone, otpCode }, type: { act: 'loginWithOTP' } },
            () => { setBusy(false); setError('הקוד שגוי או שפג תוקפו'); },
            (res) => { setBusy(false); onLogin(res); },
        );
    };

    // REGISTER step 1 — validate details, then registerUser sends the real OTP (SMS + email).
    const doRegister = () => {
        setError('');
        if (!isValidPhone(phone)) { setError('מספר טלפון לא תקין (05 ואחריו 8 ספרות)'); return; }
        if (!privateName.trim() || !lastName.trim()) { setError('נא להזין שם פרטי ושם משפחה'); return; }
        if (!isValidEmail(email)) { setError('כתובת אימייל לא תקינה'); return; }
        if (password.length < 6) { setError('הסיסמה חייבת להכיל לפחות 6 תווים'); return; }
        if (password !== confirm) { setError('הסיסמאות אינן תואמות'); return; }
        setBusy(true);
        managedata.sendData(
            { data: { phone }, type: { act: 'registerUser' } },
            (msg, code) => {
                setBusy(false);
                if (code === 415) { setError('המספר כבר רשום. עברו להתחברות.'); switchMode('login'); }
                else setError('ההרשמה נכשלה, נסו שוב');
            },
            () => { setBusy(false); setRegisterStep('code'); setOtpCode(''); },
        );
    };

    // REGISTER step 2 — verify the REAL entered code and finish creating the account.
    const doCompleteRegister = () => {
        setError('');
        if (!/^\d{4,6}$/.test(otpCode)) { setError('הזינו את הקוד שקיבלתם'); return; }
        setBusy(true);
        managedata.sendData(
            { data: { phone, privateName, lastName, email, password, otpCode }, type: { act: 'completeRegisterUser' } },
            (msg, code) => {
                setBusy(false);
                if (code === 318) setError('הקוד שגוי או שפג תוקפו');
                else setError('ההרשמה נכשלה, נסו שוב');
            },
            (res) => { setBusy(false); onLogin(res); },
        );
    };

    // Auto-submit the moment a full 6-digit code is entered (no button press needed).
    useEffect(() => {
        if (busy || otpCode.length !== 6) return;
        if (mode === 'login' && loginStep === 'code') doLoginOtp();
        else if (mode === 'register' && registerStep === 'code') doCompleteRegister();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpCode]);

    const submit = () => {
        if (busy) return;
        if (mode === 'register') return registerStep === 'details' ? doRegister() : doCompleteRegister();
        return loginStep === 'phone' ? doSendOtp() : doLoginOtp();
    };
    const onKey = (e) => { if (e.key === 'Enter') submit(); };

    const btnLabel = busy ? 'רגע…'
        : mode === 'register' ? (registerStep === 'details' ? 'שלחו לי קוד' : 'סיום הרשמה')
        : loginStep === 'phone' ? 'שלחו לי קוד' : 'התחברות';

    return (
        <section className="au-page">
            <div className="au-card">
                <Link to="/welcome" className="au-home">‹ חזרה לדף הבית</Link>
                <div className="au-brand">
                    <img className="au-logo" src={logo} alt="HOPpA" />
                </div>

                <div className="au-tabs" role="tablist">
                    <button role="tab" aria-selected={mode === 'login'}
                        className={`au-tab ${mode === 'login' ? 'is-active' : ''}`}
                        onClick={() => switchMode('login')}>התחברות</button>
                    <button role="tab" aria-selected={mode === 'register'}
                        className={`au-tab ${mode === 'register' ? 'is-active' : ''}`}
                        onClick={() => switchMode('register')}>הרשמה</button>
                    <span className={`au-tabs-ind ${mode === 'register' ? 'right' : 'left'}`} aria-hidden="true" />
                </div>

                <p className="au-sub">
                    {mode === 'register'
                        ? registerStep === 'details'
                            ? 'פתחו חשבון חדש — נשלח קוד אימות ב־SMS ובמייל'
                            : <>הזינו את הקוד שנשלח ל-<b dir="ltr">{phone}</b></>
                        : loginStep === 'phone'
                            ? 'הזינו טלפון ונשלח לכם קוד חד-פעמי ב־SMS ובמייל'
                            : <>הזינו את הקוד שנשלח ל-<b dir="ltr">{phone}</b></>}
                </p>

                {mode === 'register' && registerStep === 'details' && (
                    <div className="au-row">
                        <div className="au-field">
                            <label className="au-label" htmlFor="au-first">שם פרטי</label>
                            <input id="au-first" className="au-input" type="text" value={privateName}
                                onChange={(e) => setPrivateName(e.target.value)} onKeyDown={onKey} />
                        </div>
                        <div className="au-field">
                            <label className="au-label" htmlFor="au-last">שם משפחה</label>
                            <input id="au-last" className="au-input" type="text" value={lastName}
                                onChange={(e) => setLastName(e.target.value)} onKeyDown={onKey} />
                        </div>
                    </div>
                )}

                {/* Phone: shown in register details step, and in login step 1 */}
                {((mode === 'register' && registerStep === 'details') || (mode === 'login' && loginStep === 'phone')) && (
                    <div className="au-field">
                        <label className="au-label" htmlFor="au-phone">מספר טלפון</label>
                        <input id="au-phone" className="au-input" type="tel" inputMode="numeric" dir="ltr"
                            placeholder="050-0000000" value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} onKeyDown={onKey} />
                    </div>
                )}

                {/* OTP code: login step 2 OR register step 2 */}
                {((mode === 'login' && loginStep === 'code') || (mode === 'register' && registerStep === 'code')) && (
                    <div className="au-field">
                        <label className="au-label" htmlFor="au-otp">קוד אימות</label>
                        <input id="au-otp" className="au-input au-otp" type="text" inputMode="numeric" dir="ltr"
                            maxLength={6} autoFocus placeholder="••••••" value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={onKey} />
                    </div>
                )}

                {mode === 'register' && registerStep === 'details' && (
                    <div className="au-field">
                        <label className="au-label" htmlFor="au-email">אימייל</label>
                        <input id="au-email" className="au-input" type="email" dir="ltr"
                            placeholder="name@email.com" value={email}
                            onChange={(e) => setEmail(e.target.value)} onKeyDown={onKey} />
                    </div>
                )}

                {mode === 'register' && registerStep === 'details' && (
                    <div className="au-field">
                        <label className="au-label" htmlFor="au-pass">סיסמה</label>
                        <input id="au-pass" className="au-input" type="password" value={password}
                            placeholder="לפחות 6 תווים"
                            onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey} />
                    </div>
                )}

                {mode === 'register' && registerStep === 'details' && (
                    <div className="au-field">
                        <label className="au-label" htmlFor="au-confirm">אימות סיסמה</label>
                        <input id="au-confirm" className="au-input" type="password" value={confirm}
                            onChange={(e) => setConfirm(e.target.value)} onKeyDown={onKey} />
                    </div>
                )}

                {error && <div className="au-error">{error}</div>}

                <button className="au-btn" onClick={submit} disabled={busy}>{btnLabel}</button>

                {mode === 'login' && loginStep === 'code' && (
                    <div className="au-foot">
                        <button className="au-link" onClick={doSendOtp} disabled={busy}>שליחת קוד מחדש</button>
                        <span> · </span>
                        <button className="au-link" onClick={() => { setLoginStep('phone'); setOtpCode(''); setError(''); }}>החלפת מספר</button>
                    </div>
                )}

                {mode === 'register' && registerStep === 'code' && (
                    <div className="au-foot">
                        <button className="au-link" onClick={doRegister} disabled={busy}>שליחת קוד מחדש</button>
                        <span> · </span>
                        <button className="au-link" onClick={() => { setRegisterStep('details'); setOtpCode(''); setError(''); }}>חזרה לפרטים</button>
                    </div>
                )}

                <div className="au-foot">
                    {mode === 'login' ? (
                        <span>אין לכם חשבון? <button className="au-link" onClick={() => switchMode('register')}>להרשמה</button></span>
                    ) : (
                        <span>כבר רשומים? <button className="au-link" onClick={() => switchMode('login')}>להתחברות</button></span>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AuthPage;
